import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box, type DesignSystemTextFieldProps, Text } from '@factorialco/deprecated-design-system'
import { ContractsValueObjectsAmountStrategyTypeEnum } from 'generated/resources/graphql'

import DurationInput from 'modules/core/components/DurationInput'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import i18n from 'modules/core/lib/i18n'

import { MoneyInputField } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/MoneyInputField'
import { NumberInputField } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/NumberInputField'
import { usePayrollConceptCompensationUnits } from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

type AmountStrategyInputsFieldsProps = DesignSystemTextFieldProps & {
  amountStrategyType: ContractsValueObjectsAmountStrategyTypeEnum
  readonly?: boolean
}

function AmountStrategyInputsFields({
  amountStrategyType,
  ...props
}: AmountStrategyInputsFieldsProps) {
  const { setValue } = useFormContext()

  React.useEffect(() => {
    if (amountStrategyType === 'contracts_fixed_amount_strategy') {
      setValue('amountStrategy.__typename', 'ContractsFixedAmountStrategy')
    } else if (amountStrategyType === 'contracts_variable_amount_strategy') {
      setValue('amountStrategy.__typename', 'ContractsVariableAmountStrategy')
    }
  }, [setValue, amountStrategyType])

  if (amountStrategyType === 'contracts_fixed_amount_strategy') {
    return <FixedAmountInput ariaLabel="Fixed Amount" {...props} />
  } else if (amountStrategyType === 'contracts_variable_amount_strategy') {
    return <VariableAmountInput ariaLabel="Variable Amount" {...props} />
  }

  return null
}

function FixedAmountInput({ ...props }: { label?: string; ariaLabel?: string }) {
  const {
    control,
    getValues,
    formState: { errors },
    register,
    watch,
  } = useFormContext()
  const payrollConceptUnit = watch('payrollConcept.unitType')
  const unit = usePayrollConceptCompensationUnits(payrollConceptUnit)

  const amount = getValues('amountStrategy.amount')

  return (
    <Box flexDirection="row" minWidth="s400">
      <Controller
        name="amountStrategy.amount"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <>
            {payrollConceptUnit === 'money' ? (
              <MoneyInputField
                {...props}
                {...field}
                label={'Amount'}
                name="amountStrategy.amount"
                placeholder={i18n.t('compensations.common.placeholder')}
                mandatory
                indicator={unit}
                defaultValue={amount}
              />
            ) : payrollConceptUnit === 'time' ? (
              <Box width="full">
                <Controller
                  control={control}
                  name="amountStrategy.amount"
                  render={({ field }) => {
                    return <DurationInput {...field} label={'Amount'} minutes={field.value ?? 0} />
                  }}
                />
              </Box>
            ) : (
              <NumberInputField
                control={control}
                placeholder={i18n.t('compensations.common.placeholder')}
                label={'Amount'}
                name="amountStrategy.amount"
                indicator={unit}
              />
            )}
            <Text size="300">
              {errors?.['amountStrategy.amount']?.message
                ? String(errors?.['amountStrategy.amount']?.message)
                : ''}{' '}
            </Text>
          </>
        )}
      />
      <input
        {...register('amountStrategy.__typename', { value: 'ContractsFixedAmountStrategy' })}
        type="hidden"
      />
    </Box>
  )
}

function VariableAmountInput({ ...props }: { label?: string; ariaLabel?: string }) {
  const {
    control,
    getValues,
    formState: { errors },
    register,
    watch,
  } = useFormContext()
  const payrollConceptUnit = watch('payrollConcept.unitType')
  const unit = usePayrollConceptCompensationUnits(payrollConceptUnit)

  const upperLimit = getValues('amountStrategy.upperLimit')

  return (
    <Box flexDirection="row" minWidth="s400">
      <Controller
        name="amountStrategy.upperLimit"
        control={control}
        render={({ field }) => (
          <>
            {payrollConceptUnit === 'money' ? (
              <MoneyInputField
                {...props}
                {...field}
                label={'Up to'}
                name="amountStrategy.upperLimit"
                placeholder={'No limit'}
                mandatory
                indicator={unit}
                defaultValue={upperLimit}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'contract_versions.additional_compensation.additional_compensation_form_fields.amount_strategy_inputs_fields.variable_amount_input_information_tooltip'
                    )}
                    icon="info"
                  />
                }
              />
            ) : payrollConceptUnit === 'time' ? (
              <Box width="full">
                <Controller
                  control={control}
                  name="amountStrategy.upperLimit"
                  render={({ field }) => {
                    // TODO: Duration input doesn't support secondary label yet.
                    return <DurationInput {...field} label={'Up to'} minutes={upperLimit ?? 0} />
                  }}
                />
              </Box>
            ) : (
              <NumberInputField
                control={control}
                placeholder={'No limit'}
                label={'Up to'}
                name="amountStrategy.upperLimit"
                indicator={unit}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'contract_versions.additional_compensation.additional_compensation_form_fields.amount_strategy_inputs_fields.variable_amount_input_information_tooltip'
                    )}
                    icon="info"
                  />
                }
              />
            )}
            <Text size="300">
              {errors?.['amountStrategy.upperLimit']?.message
                ? String(errors?.['amountStrategy.upperLimit']?.message)
                : ''}{' '}
            </Text>
          </>
        )}
      />
      <input
        {...register('amountStrategy.__typename', { value: 'ContractsVariableAmountStrategy' })}
        type="hidden"
      />
    </Box>
  )
}

export { AmountStrategyInputsFields }
